import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useState,
} from 'react';
import { IPatientContext, Patient } from '../../models/patient';
import UpdatePatientDetails from '../../services/patients/updatePatientDetails';
import { getPatient } from './get-patient';

export const PatientContext = createContext<IPatientContext | null>(null);

const getPatientWithoutAddress = (patient: Patient) => {
  const addressFields: (keyof Patient)[] = [
    'address',
    'address2',
    'city',
    'country',
    'state',
    'zip',
  ];

  return (Object.keys(patient) as Array<keyof Patient>).reduce<Patient>(
    (patientWithoutAddress, key) => {
      if (!addressFields.includes(key)) {
        return { ...patientWithoutAddress, [key]: patient[key] };
      }

      return patientWithoutAddress;
    },
    {}
  );
};

export const PatientContextProvider: FC<PropsWithChildren> = (props) => {
  const [patient, setPatient] = useState<Patient | null>(null);

  /**
   * Merge new patient data with existing fields and update the patient
   */
  const updatePatient = async (
    patientId: string,
    data: Partial<Patient> & { isEuPatient?: boolean },
  ) => {
    if (!patient) {
      throw new Error('No patient in context');
    }

    await UpdatePatientDetails(patientId, { ...data });
    // eslint-disable-next-line no-param-reassign
    delete data.isEuPatient;
    const newPatient = { ...getPatientWithoutAddress(patient), ...data };

    // TODO: return patient from UpdatePatientDetails backend so client state matches backend
    setPatient(newPatient);
    return newPatient;
  };

  return (
    <PatientContext.Provider
      value={{
        patient,
        setPatient,
        getPatient: getPatient(setPatient),
        updatePatient,
      }}
      {...props}
    />
  );
};

export const usePatientContext = () => {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error(
      'usePatientContext should be used within PatientContextProvider'
    );
  }
  return context;
};
